import { useCallback, useEffect, useRef, useState } from 'react';
import { apiGet } from '../api';
import { getDisplayError } from '../apiErrors';
import { playNotificationSound } from '../lib/notificationSound';

export const CONVERSATIONS_RELOAD_EVENT = 'wapharma-conversations-reload';

export type ConversationSummary = {
  id: string;
  audience: 'INTERNAL' | 'CLIENT';
  entityType: string | null;
  entityId: string | null;
  subject: string | null;
  status: string;
  channel: string | null;
  clientContact: string | null;
  displayTitle: string;
  displaySubtitle: string | null;
  displayAvatarUrl?: string | null;
  displayFacepile?: Array<{ userId?: string; label: string; src: string | null }>;
  /** StaffDirect peer (viewer’s counterpart), else null. */
  peerUserId?: string | null;
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
  lastMessageFromViewer: boolean;
  unread: boolean;
};

type InboxFilters = {
  page?: number;
  limit?: number;
  audience?: 'INTERNAL' | 'CLIENT' | '';
  status?: 'OPEN' | 'CLOSED' | '';
  channel?: 'EMAIL' | 'WHATSAPP' | '';
  q?: string;
  unreadOnly?: boolean;
  excludeEntityTypes?: string[];
};

type LiveOptions = {
  /** Poll while the tab is visible (default false). */
  live?: boolean;
  /** Poll interval when `live` (default 12s). */
  pollMs?: number;
  /** Chime + broadcast when unreadTotal rises (default false). */
  notify?: boolean;
};

const DEFAULT_POLL_MS = 12_000;

export function useConversationList(
  token: string | null,
  filters: InboxFilters,
  options: LiveOptions = {},
) {
  const { live = false, pollMs = DEFAULT_POLL_MS, notify = false } = options;
  const [items, setItems] = useState<ConversationSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlightRef = useRef<Promise<void> | null>(null);
  const prevUnreadRef = useRef<number | null>(null);
  const excludeEntityTypesKey = filters.excludeEntityTypes?.join(',') ?? '';

  const refresh = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!token) return;

      if (inFlightRef.current) {
        return inFlightRef.current;
      }

      const silent = Boolean(opts?.silent);

      const task = (async () => {
        if (!silent) {
          setLoading(true);
          setError(null);
        }
        try {
          const params = new URLSearchParams();
          if (filters.page) params.set('page', String(filters.page));
          if (filters.limit) params.set('limit', String(filters.limit));
          if (filters.audience) params.set('audience', filters.audience);
          if (filters.status) params.set('status', filters.status);
          if (filters.channel) params.set('channel', filters.channel);
          if (filters.q?.trim()) params.set('q', filters.q.trim());
          if (filters.unreadOnly) params.set('unreadOnly', 'true');
          if (filters.excludeEntityTypes?.length) {
            params.set('excludeEntityTypes', filters.excludeEntityTypes.join(','));
          }
          const qs = params.toString();
          const res = await apiGet<{
            data: { items: ConversationSummary[]; total: number; unreadTotal: number };
          }>(`/conversations/inbox${qs ? `?${qs}` : ''}`, token);
          const nextItems = res.data.items ?? [];
          const nextTotal = res.data.total ?? 0;
          const nextUnread = res.data.unreadTotal ?? 0;
          setItems(nextItems);
          setTotal(nextTotal);
          setUnreadTotal(nextUnread);
          if (silent) setError(null);

          if (notify) {
            const prev = prevUnreadRef.current;
            if (prev !== null && nextUnread > prev) {
              playNotificationSound();
              window.dispatchEvent(new Event(CONVERSATIONS_RELOAD_EVENT));
            }
            prevUnreadRef.current = nextUnread;
          }
        } catch (e: unknown) {
          if (!silent) setError(getDisplayError(e));
        } finally {
          if (!silent) setLoading(false);
        }
      })();

      inFlightRef.current = task;
      try {
        await task;
      } finally {
        if (inFlightRef.current === task) {
          inFlightRef.current = null;
        }
      }
    },
    [
      filters.audience,
      filters.channel,
      filters.limit,
      filters.page,
      filters.q,
      filters.status,
      filters.unreadOnly,
      excludeEntityTypesKey,
      notify,
      token,
    ],
  );

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const onReload = () => {
      void refresh({ silent: true });
    };
    window.addEventListener('wapharma-inbox-reload', onReload);
    window.addEventListener(CONVERSATIONS_RELOAD_EVENT, onReload);
    return () => {
      window.removeEventListener('wapharma-inbox-reload', onReload);
      window.removeEventListener(CONVERSATIONS_RELOAD_EVENT, onReload);
    };
  }, [refresh]);

  useEffect(() => {
    if (!live || !token) return;

    let timer: ReturnType<typeof setInterval> | null = null;

    const stop = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };

    const start = () => {
      if (timer || document.hidden) return;
      timer = setInterval(() => void refresh({ silent: true }), pollMs);
    };

    const onVisibility = () => {
      if (document.hidden) {
        stop();
        return;
      }
      void refresh({ silent: true });
      start();
    };

    const onFocus = () => {
      if (!document.hidden) void refresh({ silent: true });
    };

    start();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    };
  }, [live, pollMs, refresh, token]);

  return { items, total, unreadTotal, loading, error, refresh };
}
